import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const fullFaqItems = [
  {
    q: '未境原創 VIJING 是什麼品牌？',
    a: '未境原創 VIJING 以愛情小說為主要出版方向，本品牌委託崧博出版經銷，官方網站由崧博出版製作。',
  },
  {
    q: '未境原創主要出版哪些內容？',
    a: '未境原創主要出版愛情小說，涵蓋原創愛情、古代言情、情感故事與人物成長等方向。完整書目請以崧博書籍資料庫為準。',
  },
  {
    q: '未境原創的故事有什麼特色？',
    a: '未境原創重視人物關係與情感成長，除了描寫相遇和心動，也關注角色的背景、傷口、選擇、理解與改變。',
  },
  {
    q: '未境原創和崧博出版是什麼關係？',
    a: '未境原創 VIJING 委託崧博出版經銷，官方網站由崧博出版製作。相關作品可透過崧博出版合作通路及書籍資料庫查詢。',
  },
  {
    q: '未境原創的作品在哪裡可以購買？',
    a: '讀者可透過崧博出版合作通路查找未境原創作品；實際上架狀態請以各平台頁面為準。',
  },
];

function AnimatedSection({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

export default function FAQPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen-dynamic flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Japanese%20tea%20room%20interior%20with%20soft%20natural%20light%20through%20washi%20paper%20shoji%20screens%2C%20low%20wooden%20table%20with%20ceramic%20tea%20bowl%20and%20brush%20calligraphy%20set%2C%20warm%20cream%20and%20muted%20beige%20tones%20with%20gentle%20sage%20accents%2C%20serene%20meditative%20atmosphere%20with%20wabi%20sabi%20aesthetic%2C%20minimalist%20interior%20photography%20with%20organic%20textures%20and%20tranquil%20contemplative%20mood&width=1600&height=600&seq=faq-hero-jp-2026&orientation=landscape"
              alt="日本茶室與障子窗柔和光線"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-900/70"></div>
          </div>
          <div className="relative z-10 section-container text-center w-full pt-[72px] pb-16 md:pb-0">
            <span className="inline-block px-3 py-1 text-xs font-label bg-white/15 text-white rounded-full backdrop-blur-sm border border-white/20 mb-4">
              常見問答
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-semibold mb-4 leading-tight text-white text-shadow-hero-light">
              常見問答
            </h1>
            <p className="text-sm md:text-base text-white/75 max-w-2xl mx-auto leading-relaxed text-shadow-hero-light">
              關於未境原創 VIJING 的常見問題
            </p>
          </div>
        </section>

        {/* FAQ List */}
        <section className="min-h-screen-dynamic flex items-center bg-background-50">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full py-16 md:py-0">
            <div className="text-center mb-10 md:mb-12">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                常見問題
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                關於未境原創的常見問題
              </h2>
            </div>

            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 space-y-3">
              {fullFaqItems.map((item, idx) => (
                <details key={idx} className="group bg-background-100 border border-background-200/70 rounded-xl overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 md:p-6 text-foreground-800 text-sm md:text-base font-label font-medium list-none cursor-pointer">
                    <span className="pr-4 text-left">{item.q}</span>
                    <span className="w-6 h-6 flex items-center justify-center shrink-0 rounded-full bg-background-50 group-open:bg-accent-50 transition-colors">
                      <i className="ri-add-line text-xs text-foreground-500 group-open:hidden"></i>
                      <i className="ri-subtract-line text-xs text-accent-500 hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                    <p className="text-foreground-500 text-sm leading-relaxed text-left">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>

            <div className="text-center mt-10 pt-8 border-t border-background-200/70">
              <p className="text-sm text-foreground-500 mb-4">
                品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://sonbook.net"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  前往崧博出版
                </a>
                <Link
                  to="/stores"
                  className="px-6 py-3 text-sm font-label bg-secondary-500 text-white rounded-full hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看合作購書通路
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </section>
      </main>
      <Footer />
    </>
  );
}